import React, { useState } from 'react';

import {
  Avatar,
  Box,
  CircularProgress,
  createStyles,
  makeStyles,
  Tooltip,
  Typography,
  useTheme,
} from '@material-ui/core';
import clsx from 'clsx';
import { FiMessageCircle } from 'react-icons/fi';

import { AppLink } from './AppLink';
import { RandomLogo } from './RandomLogo';
import { getColor, monochrome } from '../theme/styles';
import { AppIconButton } from './AppIconButton';

const useStyles = makeStyles(theme =>
  createStyles({
    tableAvatar: {
      width: 35,
      height: 35,
    },
    bold: {
      fontWeight: 500,
    },

    primaryBorder: {
      border: `2px solid ${theme.palette.primary.main}`,
      borderRadius: '50%',
    },
    warningBorder: {
      border: `2px solid ${theme.palette.warning.main}`,
      borderRadius: '50%',
    },
    neutralBorder: {
      border: `2px solid ${monochrome.mediumlight}`,
      borderRadius: '50%',
    },
    name: {
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    },
  })
);

interface Borders {
  primaryBorder?: boolean;
  warningBorder?: boolean;
  neutralBorder?: boolean;
}

interface Props extends Borders {
  avatarUrl?: string | null;
  avatarPlaceholder?: string;
  link?: string;
  name: string;
  tooltip?: string;
  icon?: React.ReactElement;
  target?: React.HTMLAttributeAnchorTarget;
  createPrivateChat?: () => Promise<void>;
  hideAvatar?: boolean;
}

const RoundAvatarBorder = ({
  warningBorder,
  primaryBorder,
  neutralBorder,
  children,
}: { children: React.ReactNode } & Borders) => {
  const classes = useStyles();

  return (
    <Box
      className={clsx({
        [classes.primaryBorder]: primaryBorder,
        [classes.warningBorder]: warningBorder,
        [classes.neutralBorder]: neutralBorder,
      })}
    >
      {children}
    </Box>
  );
};

export const GridCellWithAvatar: React.FC<Props> = ({
  avatarUrl,
  avatarPlaceholder,
  link,
  name,
  icon,
  target = '_self',
  createPrivateChat,
  hideAvatar = false,
  ...rest
}) => {
  const classes = useStyles();
  const theme = useTheme();
  const colors = getColor(theme.palette.type === 'dark');
  const [isLoading, setIsLoading] = useState(false);

  const handleCreateChat = async () => {
    if (createPrivateChat) {
      setIsLoading(true);
      await createPrivateChat();
      setIsLoading(false);
    }
  };

  return (
    <Box display="flex" paddingRight={1} alignItems="center" width="100%" cy-test-id="grid-cell-with-avatar">
      {!hideAvatar && (
        <Box marginRight={1}>
          <RoundAvatarBorder {...rest}>
            {!avatarUrl && !avatarPlaceholder ? (
              <RandomLogo name={name} />
            ) : (
              <Avatar
                imgProps={{ loading: 'lazy' }}
                className={classes.tableAvatar}
                src={avatarUrl || avatarPlaceholder}
              ></Avatar>
            )}
          </RoundAvatarBorder>
        </Box>
      )}

      <Box
        display="flex"
        gridGap={8}
        alignItems="center"
        width="100%"
        justifyContent="space-between"
        overflow="hidden"
      >
        {link ? (
          <AppLink to={link} target={target} className={classes.name}>
            {name}
          </AppLink>
        ) : (
          <Typography className={clsx(classes.bold, classes.name)} variant="caption">
            {name}
          </Typography>
        )}

        <Box display="flex" alignItems="center" gridGap={8}>
          {!!createPrivateChat && (
            <AppIconButton
              isSmall
              backgroundColor={colors.secondaryBG}
              variant="paper"
              onClick={handleCreateChat}
              disabled={isLoading}
            >
              {isLoading ? (
                <CircularProgress size={14} />
              ) : (
                <FiMessageCircle size={14} color={colors.secondaryDark} />
              )}
            </AppIconButton>
          )}

          {icon && (
            <Box display="flex" alignItems="center">
              {icon}
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
};
